import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Tabs, Tab, Box,
  FormControl, InputLabel, Select, MenuItem, Button, Typography, TextField,
  IconButton, Tooltip, Chip, Divider, Stack, CircularProgress, List, ListItem,
  ListItemText, Avatar, Alert, InputAdornment, useTheme, alpha,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import SendIcon from "@mui/icons-material/Send";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import RestoreIcon from "@mui/icons-material/Restore";
import HistoryIcon from "@mui/icons-material/History";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import {
  getEstadosSeguimiento, crearEstadoSeguimiento, editarEstadoSeguimiento, deshabilitarEstadoSeguimiento,
  getSeguimiento, actualizarEstadoSeguimiento, agregarObservacion,
} from "../../http/actions/SeguimientoAction";

// Paleta por defecto para nuevos estados
const COLORES_ESTADO = [
  "#43A047", "#FB8C00", "#E53935", "#1E88E5", "#8E24AA",
  "#00897B", "#6D4C41", "#546E7A", "#C0CA33", "#F4511E",
];

const formatFechaHora = (value) =>
  value
    ? new Date(value).toLocaleString("es-ES", {
        day: "2-digit", month: "2-digit", year: "numeric",
        hour: "2-digit", minute: "2-digit",
      })
    : "";

const inicial = (texto) => (texto ? texto.trim().charAt(0).toUpperCase() : "?");

const SeguimientoNormativaModal = ({
  open, onClose, token, idUsuario, normativa,
  clientesUsuario = [], onSeguimientoActualizado,
}) => {
  const theme = useTheme();
  const [tab, setTab] = useState(0);

  // Clientes elegibles: intersección entre los clientes de la norma y los del usuario.
  // Si la norma no trae clientes, caemos a los del usuario.
  const clientesElegibles = useMemo(() => {
    const deNorma = Array.isArray(normativa?.clientes) ? normativa.clientes : [];
    if (deNorma.length > 0) return deNorma;
    return clientesUsuario;
  }, [normativa, clientesUsuario]);

  const [idCliente, setIdCliente] = useState(null);

  // ── Estado del seguimiento ──
  const [seguimiento, setSeguimiento] = useState(null);
  const [estados, setEstados] = useState([]);
  const [estadoSeleccionado, setEstadoSeleccionado] = useState("");
  const [nuevoComentario, setNuevoComentario] = useState("");

  const [cargandoSeguimiento, setCargandoSeguimiento] = useState(false);
  const [cargandoEstados, setCargandoEstados] = useState(false);
  const [guardandoEstado, setGuardandoEstado] = useState(false);
  const [enviandoComentario, setEnviandoComentario] = useState(false);
  const [error, setError] = useState("");

  // ── Gestión de estados (pestaña 2) ──
  const [nuevoEstadoNombre, setNuevoEstadoNombre] = useState("");
  const [nuevoEstadoColor, setNuevoEstadoColor] = useState(COLORES_ESTADO[0]);
  const [creandoEstado, setCreandoEstado] = useState(false);
  const [editandoEstadoId, setEditandoEstadoId] = useState(null); // null = creando; id = editando ese estado

  // Resolver cliente inicial al abrir
  useEffect(() => {
    if (!open) return;
    setTab(0);
    setError("");
    setEditandoEstadoId(null);
    setNuevoEstadoNombre("");
    if (clientesElegibles.length === 1) {
      setIdCliente(clientesElegibles[0].id);
    } else {
      setIdCliente(null);
    }
  }, [open, clientesElegibles]);

  // Si cambia el cliente, salir de cualquier edición en curso (los estados son de otro cliente)
  useEffect(() => {
    setEditandoEstadoId(null);
    setNuevoEstadoNombre("");
  }, [idCliente]);

  const clienteNombre = useMemo(
    () => clientesElegibles.find((c) => c.id === idCliente)?.nombre || "",
    [clientesElegibles, idCliente]
  );

  // ── Cargas ──
  const cargarEstados = useCallback(() => {
    if (!idCliente) return;
    setCargandoEstados(true);
    getEstadosSeguimiento(token, idCliente)
      .then((res) => setEstados(res?.items || []))
      .catch(() => setEstados([]))
      .finally(() => setCargandoEstados(false));
  }, [token, idCliente]);

  const cargarSeguimiento = useCallback(() => {
    if (!idCliente || !normativa?.id) return;
    setCargandoSeguimiento(true);
    setError("");
    getSeguimiento(token, idUsuario, normativa.id, idCliente)
      .then((res) => {
        const item = res?.item || null;
        setSeguimiento(item);
        setEstadoSeleccionado(item?.estadoSeguimientoId || "");
      })
      .catch((e) => {
        setSeguimiento(null);
        setError(typeof e === "string" ? e : "No se pudo cargar el seguimiento.");
      })
      .finally(() => setCargandoSeguimiento(false));
  }, [token, idUsuario, normativa, idCliente]);

  useEffect(() => {
    if (open && idCliente) {
      cargarEstados();
      cargarSeguimiento();
    }
  }, [open, idCliente, cargarEstados, cargarSeguimiento]);

  // Estados habilitados para el selector; si el estado actual está deshabilitado, lo incluimos igual
  const estadosParaSelector = useMemo(() => {
    const habilitados = estados.filter((e) => e.habilitado);
    if (
      seguimiento?.estadoSeguimientoId &&
      !habilitados.some((e) => e.id === seguimiento.estadoSeguimientoId)
    ) {
      return [
        ...habilitados,
        {
          id: seguimiento.estadoSeguimientoId,
          nombre: `${seguimiento.estadoNombre} (deshabilitado)`,
          color: seguimiento.estadoColor,
          habilitado: false,
        },
      ];
    }
    return habilitados;
  }, [estados, seguimiento]);

  // ── Acciones ──
  const handleGuardarEstado = () => {
    if (!estadoSeleccionado || !idCliente) return;
    setGuardandoEstado(true);
    setError("");
    actualizarEstadoSeguimiento(token, {
      idUsuario,
      idNormativa: normativa.id,
      idCliente,
      estadoSeguimientoId: estadoSeleccionado,
    })
      .then(() => {
        cargarSeguimiento();
        onSeguimientoActualizado?.(normativa.id);
      })
      .catch((e) => setError(typeof e === "string" ? e : "No se pudo actualizar el estado."))
      .finally(() => setGuardandoEstado(false));
  };

  const handleAgregarComentario = () => {
    const comentario = nuevoComentario.trim();
    if (!comentario || !idCliente) return;
    setEnviandoComentario(true);
    setError("");
    agregarObservacion(token, {
      idUsuario,
      idNormativa: normativa.id,
      idCliente,
      comentario,
    })
      .then(() => {
        setNuevoComentario("");
        cargarSeguimiento();
        onSeguimientoActualizado?.(normativa.id);
      })
      .catch((e) => setError(typeof e === "string" ? e : "No se pudo agregar el comentario."))
      .finally(() => setEnviandoComentario(false));
  };

  const resetFormularioEstado = () => {
    setNuevoEstadoNombre("");
    setNuevoEstadoColor(COLORES_ESTADO[(estados.length + 1) % COLORES_ESTADO.length]);
    setEditandoEstadoId(null);
  };

  const handleIniciarEdicion = (estado) => {
    setEditandoEstadoId(estado.id);
    setNuevoEstadoNombre(estado.nombre);
    setNuevoEstadoColor(estado.color || COLORES_ESTADO[0]);
    setError("");
  };

  const handleCancelarEdicion = () => {
    resetFormularioEstado();
  };

  // Crea un estado nuevo o guarda los cambios del que se está editando
  const handleGuardarEstadoForm = () => {
    const nombre = nuevoEstadoNombre.trim();
    if (!nombre || !idCliente) return;
    setCreandoEstado(true);
    setError("");

    const enEdicion = editandoEstadoId != null;
    const peticion = enEdicion
      ? editarEstadoSeguimiento(token, {
          id: editandoEstadoId,
          idCliente,
          nombre,
          color: nuevoEstadoColor,
          orden: estados.find((e) => e.id === editandoEstadoId)?.orden ?? estados.length + 1,
        })
      : crearEstadoSeguimiento(token, {
          idCliente,
          nombre,
          color: nuevoEstadoColor,
          orden: estados.length + 1,
        });

    peticion
      .then(() => {
        resetFormularioEstado();
        cargarEstados();
        // Si editamos el estado que está asignado en el seguimiento actual, refrescamos para ver el nuevo color/nombre
        if (enEdicion) {
          cargarSeguimiento();
          onSeguimientoActualizado?.(normativa.id);
        }
      })
      .catch((e) =>
        setError(typeof e === "string" ? e : `No se pudo ${enEdicion ? "guardar el cambio" : "crear el estado"}.`)
      )
      .finally(() => setCreandoEstado(false));
  };

  const handleToggleEstado = (estado) => {
    // Si desactivamos el estado que estamos editando, salimos del modo edición
    if (editandoEstadoId === estado.id) resetFormularioEstado();
    deshabilitarEstadoSeguimiento(token, estado.id)
      .then(() => cargarEstados())
      .catch((e) => setError(typeof e === "string" ? e : "No se pudo cambiar el estado."));
  };

  const observaciones = seguimiento?.observaciones || [];
  const necesitaElegirCliente = !idCliente;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        <Typography variant="h6" component="div" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
          Seguimiento
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, wordBreak: "break-word" }}>
          {normativa?.nombre}
        </Typography>
        <IconButton onClick={onClose} sx={{ position: "absolute", right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* Selector de cliente cuando el usuario tiene varios */}
      {clientesElegibles.length > 1 && (
        <Box sx={{ px: 3, pb: 1 }}>
          <FormControl fullWidth size="small">
            <InputLabel>Cliente</InputLabel>
            <Select
              value={idCliente || ""}
              label="Cliente"
              onChange={(e) => setIdCliente(e.target.value)}
            >
              {clientesElegibles.map((c) => (
                <MenuItem key={c.id} value={c.id}>{c.nombre}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}

      <Tabs
        value={tab}
        onChange={(e, v) => setTab(v)}
        sx={{ px: 3, borderBottom: 1, borderColor: "divider", minHeight: 44 }}
      >
        <Tab label="Seguimiento" sx={{ minHeight: 44, textTransform: "none" }} />
        <Tab label="Estados" sx={{ minHeight: 44, textTransform: "none" }} />
      </Tabs>

      <DialogContent sx={{ pt: 2.5, minHeight: 320 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>{error}</Alert>}

        {necesitaElegirCliente ? (
          <Box sx={{ textAlign: "center", py: 6 }}>
            <Typography variant="body2" color="text.secondary">
              Elige un cliente para ver o registrar su seguimiento.
            </Typography>
          </Box>
        ) : tab === 0 ? (
          // ─────────── PESTAÑA 1: SEGUIMIENTO ───────────
          <Box>
            {clientesElegibles.length > 1 && (
              <Typography variant="caption" color="text.secondary">
                Seguimiento para <strong>{clienteNombre}</strong>
              </Typography>
            )}

            {cargandoSeguimiento ? (
              <Box display="flex" justifyContent="center" py={4}><CircularProgress size={28} /></Box>
            ) : (
              <>
                {/* Estado actual + cambiar */}
                <Box sx={{ mt: 1.5, mb: 2 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                    Estado
                  </Typography>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <FormControl size="small" sx={{ minWidth: 200, flex: 1 }}>
                      <Select
                        value={estadoSeleccionado || ""}
                        displayEmpty
                        onChange={(e) => setEstadoSeleccionado(e.target.value)}
                        renderValue={(val) => {
                          if (!val) return <em style={{ color: theme.palette.text.disabled }}>Sin estado</em>;
                          const est = estadosParaSelector.find((x) => x.id === val);
                          if (!est) return "";
                          return (
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Box sx={{ width: 12, height: 12, borderRadius: "50%", bgcolor: est.color || "#9E9E9E" }} />
                              <span>{est.nombre}</span>
                            </Stack>
                          );
                        }}
                      >
                        {estadosParaSelector.length === 0 && (
                          <MenuItem disabled value="">
                            No hay estados. Créalos en la pestaña "Estados".
                          </MenuItem>
                        )}
                        {estadosParaSelector.map((est) => (
                          <MenuItem key={est.id} value={est.id} disabled={!est.habilitado}>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Box sx={{ width: 12, height: 12, borderRadius: "50%", bgcolor: est.color || "#9E9E9E" }} />
                              <span>{est.nombre}</span>
                            </Stack>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Button
                      variant="contained"
                      onClick={handleGuardarEstado}
                      disabled={
                        guardandoEstado ||
                        !estadoSeleccionado ||
                        estadoSeleccionado === seguimiento?.estadoSeguimientoId
                      }
                    >
                      {guardandoEstado ? <CircularProgress size={20} color="inherit" /> : "Guardar"}
                    </Button>
                  </Stack>
                  {seguimiento?.fechaModificacion && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.75 }}>
                      Última actualización: {formatFechaHora(seguimiento.fechaModificacion)}
                    </Typography>
                  )}
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Observaciones */}
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                  Observaciones
                </Typography>

                {/* Agregar comentario */}
                <TextField
                  fullWidth
                  size="small"
                  multiline
                  minRows={2}
                  placeholder="Escribe una observación…"
                  value={nuevoComentario}
                  onChange={(e) => setNuevoComentario(e.target.value)}
                  sx={{ mb: 1 }}
                />
                <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={enviandoComentario ? <CircularProgress size={16} /> : <SendIcon fontSize="small" />}
                    onClick={handleAgregarComentario}
                    disabled={enviandoComentario || !nuevoComentario.trim()}
                  >
                    Agregar
                  </Button>
                </Box>

                {/* Historial */}
                {observaciones.length === 0 ? (
                  <Box sx={{ textAlign: "center", py: 3, color: "text.secondary" }}>
                    <HistoryIcon sx={{ fontSize: 32, opacity: 0.4, mb: 0.5 }} />
                    <Typography variant="body2">Aún no hay observaciones registradas.</Typography>
                  </Box>
                ) : (
                  <List disablePadding>
                    {observaciones.map((obs) => (
                      <ListItem
                        key={obs.id}
                        alignItems="flex-start"
                        sx={{
                          px: 1.5, py: 1.25, mb: 1, borderRadius: 1.5,
                          bgcolor: alpha(theme.palette.primary.main, 0.03),
                          border: `1px solid ${theme.palette.divider}`,
                        }}
                      >
                        <Avatar sx={{ width: 30, height: 30, mr: 1.5, fontSize: "0.8rem", bgcolor: "primary.main" }}>
                          {inicial(obs.creadoPor)}
                        </Avatar>
                        <ListItemText
                          primary={
                            <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                              {obs.comentario}
                            </Typography>
                          }
                          secondary={
                            <Typography variant="caption" color="text.secondary">
                              {obs.creadoPor} · {formatFechaHora(obs.fechaCreacion)}
                            </Typography>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                )}
              </>
            )}
          </Box>
        ) : (
          // ─────────── PESTAÑA 2: ESTADOS ───────────
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
              Estados de {clienteNombre || "este cliente"}. Los que desactives dejan de aparecer al elegir estado,
              pero no se pierden en normas que ya los usan.
            </Typography>

            {/* Crear / editar estado */}
            <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mb: 1 }}>
              <TextField
                size="small"
                label={editandoEstadoId ? "Editar estado" : "Nuevo estado"}
                value={nuevoEstadoNombre}
                onChange={(e) => setNuevoEstadoNombre(e.target.value)}
                sx={{ flex: 1 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Box sx={{ width: 14, height: 14, borderRadius: "50%", bgcolor: nuevoEstadoColor }} />
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                variant="contained"
                startIcon={
                  creandoEstado
                    ? <CircularProgress size={16} color="inherit" />
                    : editandoEstadoId ? <EditOutlinedIcon /> : <AddIcon />
                }
                onClick={handleGuardarEstadoForm}
                disabled={creandoEstado || !nuevoEstadoNombre.trim()}
                sx={{ mt: 0.25 }}
              >
                {editandoEstadoId ? "Guardar" : "Crear"}
              </Button>
            </Stack>
            {editandoEstadoId && (
              <Button
                size="small"
                startIcon={<CloseOutlinedIcon fontSize="small" />}
                onClick={handleCancelarEdicion}
                sx={{ mb: 1 }}
              >
                Cancelar edición
              </Button>
            )}

            {/* Selector de color */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.75 }}>
                Color
              </Typography>
              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                {COLORES_ESTADO.map((color) => (
                  <Box
                    key={color}
                    onClick={() => setNuevoEstadoColor(color)}
                    sx={{
                      width: 24, height: 24, borderRadius: "50%", bgcolor: color, cursor: "pointer",
                      border: nuevoEstadoColor === color ? `2px solid ${theme.palette.text.primary}` : "2px solid transparent",
                      transition: "transform 0.1s",
                      "&:hover": { transform: "scale(1.15)" },
                    }}
                  />
                ))}
              </Stack>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Lista de estados */}
            {cargandoEstados ? (
              <Box display="flex" justifyContent="center" py={3}><CircularProgress size={24} /></Box>
            ) : estados.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", py: 2 }}>
                Todavía no hay estados. Crea el primero arriba.
              </Typography>
            ) : (
              <List disablePadding>
                {estados.map((est) => (
                  <ListItem
                    key={est.id}
                    sx={{
                      px: 1, py: 0.75,
                      opacity: est.habilitado ? 1 : 0.5,
                      borderRadius: 1,
                      bgcolor: editandoEstadoId === est.id ? alpha(theme.palette.primary.main, 0.08) : "transparent",
                    }}
                    secondaryAction={
                      <Stack direction="row" spacing={0.5}>
                        <Tooltip title="Editar">
                          <IconButton edge="end" size="small" onClick={() => handleIniciarEdicion(est)}>
                            <EditOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={est.habilitado ? "Desactivar" : "Reactivar"}>
                          <IconButton edge="end" size="small" onClick={() => handleToggleEstado(est)}>
                            {est.habilitado ? <DeleteOutlineIcon fontSize="small" /> : <RestoreIcon fontSize="small" />}
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    }
                  >
                    <Chip
                      size="small"
                      label={est.nombre}
                      sx={{
                        bgcolor: alpha(est.color || "#9E9E9E", 0.15),
                        color: est.color || "text.primary",
                        fontWeight: 600,
                        border: `1px solid ${alpha(est.color || "#9E9E9E", 0.4)}`,
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
};

export default SeguimientoNormativaModal;